
// CollectionHeader component - displays the header for collection views
import { Grid3X3, List, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import AddContentButton from "@/components/common/AddContentButton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { NetworkIcon } from "../icons/NetworkIcon";
import FrameIcon from "../icons/FrameIcon";
import FreeformIcon from "../icons/FreeformIcon";
import Icon from "@/components/ui/icon";
import { useEffect } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSidebar } from "@/components/ui/sidebar";
import { Link, useLocation } from "react-router-dom";
import { ViewMode } from "@/hooks/usePersistentViewMode";
import RemoveFromBoardButton from "./board/RemoveFromBoardButton";
import { TooltipProvider } from "@/components/ui/tooltip";

interface CollectionHeaderProps {
  title: string;
  description?: string;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  isBoardView?: boolean;
  refreshButton?: React.ReactNode;
}

const CollectionHeader = ({ 
  title, 
  description = "", 
  viewMode, 
  onViewModeChange,
  isBoardView = false,
  refreshButton
}: CollectionHeaderProps) => {
  const isMobile = useIsMobile();
  const { openMobile, setOpenMobile } = useSidebar();
  const location = useLocation();
  
  // Auto-detect if we're on a board page
  const isOnBoardPage = isBoardView || location.pathname.includes('/board/');
  
  // Debug logging for troubleshooting
  useEffect(() => {
    console.log("CollectionHeader rendered with:", {
      title: title || "[EMPTY TITLE]",
      description: description || "[EMPTY DESCRIPTION]",
      viewMode,
      isMobile,
      openMobile,
      isOnBoardPage
    });
  }, [title, description, viewMode, isMobile, openMobile, isOnBoardPage]);

  // Handler for mobile menu button
  const handleMenuClick = () => {
    console.log("Menu button clicked, current openMobile state:", openMobile);
    setOpenMobile(true);
  };

  // Ensure we always have a title and description
  const displayTitle = title || "Collection";
  const displayDescription = description || "";

  return (
    <TooltipProvider>
      <div 
        className={cn(
          "bg-background sticky top-0",
          isMobile ? "flex flex-col pb-4 px-7 space-y-3" : "flex items-center justify-between px-7"
        )} 
        style={isMobile ? 
          { zIndex: 20 } : 
          { 
            height: '80px',
            maxHeight: '80px',
            minHeight: '80px',
            paddingTop: '0'
          }
        }
      >
        {/* First row with title and description */}
        <div className={cn(
          "flex",
          isMobile ? "flex-row items-center justify-between w-full pt-2" : "flex-col space-y-[-4px]"
        )}>
          <div className="flex items-center">
            {isMobile && (
              <Link to="/dashboard">
                <img 
                  src="/lovable-uploads/538dfb2b-93c1-451f-9a81-7393dd3964c9.png" 
                  alt="Clustr Icon" 
                  className="h-[54px] mr-5" 
                  style={{ height: '54px', width: 'auto' }}
                />
              </Link>
            )}
            
            <div className="flex flex-col">
              <h1 className="text-[24px] font-semibold text-foreground">{displayTitle}</h1>
              <p className="text-muted-foreground h-6">{displayDescription}</p>
            </div>
          </div>
        </div>
        
        {/* Second row with all controls */}
        <div className={cn(
          "flex items-center", 
          isMobile ? "w-full justify-between mt-2" : "gap-4"
        )}>
          {/* Menu button moved to the left, aligned with other action buttons */}
          {isMobile && (
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-10 w-10 bg-muted rounded-xl hover:bg-accent"
              onClick={handleMenuClick}
            >
              <Icon 
                icon={Menu} 
                size={20} 
                strokeWidth={2}
              />
            </Button>
          )}
          
          {/* Group view toggle and add content buttons on the right */}
          <div className="flex items-center space-x-2 ml-auto">
            {isOnBoardPage && (
              <RemoveFromBoardButton className="mr-2" />
            )}
            
            {/* Display the refresh button if provided */}
            {refreshButton}
            
            <div className="bg-muted p-1 rounded-lg flex items-center">
              <ToggleGroup type="single" value={viewMode} onValueChange={(value) => value && onViewModeChange(value as ViewMode)}>
                <ToggleGroupItem value="freeform" className={cn("rounded-md h-10 w-10 flex items-center justify-center", {
                  "!bg-white dark:!bg-background shadow-sm": viewMode === "freeform",
                })}>
                  <FreeformIcon 
                    size={20} 
                    strokeWidth={2}
                    className={viewMode === "freeform" ? "text-gray-800 dark:text-gray-200" : "text-gray-400"}
                  />
                </ToggleGroupItem>
                <ToggleGroupItem value="frame" className={cn("rounded-md h-10 w-10 flex items-center justify-center", {
                  "!bg-white dark:!bg-background shadow-sm": viewMode === "frame",
                })}>
                  <FrameIcon
                    size={20}
                    strokeWidth={2}
                    className={viewMode === "frame" ? "text-gray-800 dark:text-gray-200" : "text-gray-400"}
                  />
                </ToggleGroupItem>
                <ToggleGroupItem value="grid" className={cn("rounded-md h-10 w-10 flex items-center justify-center", {
                  "!bg-white dark:!bg-background shadow-sm": viewMode === "grid",
                })}>
                  <Icon 
                    icon={Grid3X3}
                    size={20}
                    strokeWidth={2}
                    className={viewMode === "grid" ? "text-gray-800 dark:text-gray-200" : "text-gray-400"}
                  />
                </ToggleGroupItem>
                <ToggleGroupItem value="list" className={cn("rounded-md h-10 w-10 flex items-center justify-center", {
                  "!bg-white dark:!bg-background shadow-sm": viewMode === "list",
                })}>
                  <Icon 
                    icon={List}
                    size={20}
                    strokeWidth={2}
                    className={viewMode === "list" ? "text-gray-800 dark:text-gray-200" : "text-gray-400"}
                  />
                </ToggleGroupItem>
                <ToggleGroupItem value="network" className={cn("rounded-md h-10 w-10 flex items-center justify-center", {
                  "!bg-white dark:!bg-background shadow-sm": viewMode === "network",
                })}>
                  <NetworkIcon 
                    size={20} 
                    strokeWidth={2}
                    className={viewMode === "network" ? "text-gray-800 dark:text-gray-200" : "text-gray-400"}
                  />
                </ToggleGroupItem>
              </ToggleGroup>
            </div>
            
            <AddContentButton 
              variant={isMobile ? "icon" : "default"} 
              id="add-content-header"
            />
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default CollectionHeader;
